import React, { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Loader, Text } from '@vibe/core';
import { listTimesheets } from '../../api/timesheet-approval.api';
import { useApprovalStore } from '../../stores/approvalStore';
import TimesheetListRow from './TimesheetListRow';

export interface TimesheetHistoryListProps {
  currentUserId: string;
  userNameMap?: Record<string, string>;
  /** Status filter value (empty string = all) */
  statusFilter: string;
  /** Comma-separated user IDs (undefined = all) */
  userFilterParam?: string;
  /** ISO date string for "from" filter */
  fromDate: string;
  /** ISO date string for "to" filter */
  toDate: string;
}

const TimesheetHistoryList: React.FC<TimesheetHistoryListProps> = ({
  currentUserId,
  userNameMap,
  statusFilter,
  userFilterParam,
  fromDate,
  toDate,
}) => {
  const historyTimesheets = useApprovalStore((s) => s.historyTimesheets);
  const setHistoryTimesheets = useApprovalStore((s) => s.setHistoryTimesheets);
  const historyTotal = useApprovalStore((s) => s.historyTotal);
  const setHistoryTotal = useApprovalStore((s) => s.setHistoryTotal);
  const historyPage = useApprovalStore((s) => s.historyPage);
  const setHistoryPage = useApprovalStore((s) => s.setHistoryPage);
  const historyPageSize = useApprovalStore((s) => s.historyPageSize);
  const selectedTimesheetId = useApprovalStore((s) => s.selectedTimesheetId);
  const setSelectedTimesheetId = useApprovalStore(
    (s) => s.setSelectedTimesheetId,
  );

  const [loading, setLoading] = useState(false);

  // Reset to page 1 when filters change
  useEffect(() => {
    setHistoryPage(1);
  }, [statusFilter, userFilterParam, fromDate, toDate, setHistoryPage]);

  // Fetch data when filters or page change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    listTimesheets({
      approverUserId: currentUserId,
      status: statusFilter || undefined,
      userId: userFilterParam,
      from: fromDate || undefined,
      to: toDate || undefined,
      page: historyPage,
      pageSize: historyPageSize,
    })
      .then((response) => {
        if (!cancelled) {
          setHistoryTimesheets(response.data);
          setHistoryTotal(response.total);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryTimesheets([]);
          setHistoryTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentUserId,
    statusFilter,
    userFilterParam,
    fromDate,
    toDate,
    historyPage,
    historyPageSize,
    setHistoryTimesheets,
    setHistoryTotal,
  ]);

  // Pagination calculations
  const totalPages = Math.ceil(historyTotal / historyPageSize);
  const showingFrom =
    historyTotal === 0 ? 0 : (historyPage - 1) * historyPageSize + 1;
  const showingTo = Math.min(historyPage * historyPageSize, historyTotal);

  return (
    <div className="rp-ts-history-list">
      {/* List body */}
      <div className="rp-ts-history-list__body">
        {loading ? (
          <div className="rp-ts-history-list__loading">
            <Loader size={Loader.sizes.MEDIUM} />
          </div>
        ) : historyTimesheets.length === 0 ? (
          <div className="rp-ts-history-list__empty">
            <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
              No approval history found.
            </Text>
          </div>
        ) : (
          historyTimesheets.map((ts) => (
            <TimesheetListRow
              key={ts.id}
              submission={ts}
              userName={userNameMap?.[ts.userId]}
              isSelected={selectedTimesheetId === ts.id}
              onClick={() => setSelectedTimesheetId(ts.id)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {historyTotal > 0 && (
        <div className="rp-ts-history-list__pagination">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            {showingFrom}–{showingTo} of {historyTotal}
          </Text>
          <Flex gap={Flex.gaps.XS}>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={historyPage <= 1}
              onClick={() => setHistoryPage(historyPage - 1)}
            >
              Prev
            </Button>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={historyPage >= totalPages}
              onClick={() => setHistoryPage(historyPage + 1)}
            >
              Next
            </Button>
          </Flex>
        </div>
      )}
    </div>
  );
};

export default TimesheetHistoryList;
